import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { API } from 'aws-amplify';
import Box from '@material-ui/core/Box';
import Alert from '@material-ui/lab/Alert';
import Card from '@material-ui/core/Card';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CardContent from '@material-ui/core/CardContent';
import useStyles from '../styles';
import Typography from '@material-ui/core/Typography';
import Spin from 'components/common/Spin';
import Button from 'components/common/Button';
import { GET_SUBSCRIPTION_DETAILS } from 'graphql/student/subscription';
import useSnack from 'hooks/useSnack';
import { toUserTimezone } from 'utils';
import CheckoutForm from 'modules/parent/subscription/components/CheckoutForm';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUB);

const ReSubModal = ({ product, onCancel, onSubmit }) => {
  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>Subscribe to {product.package_name}</DialogTitle>
      <DialogContent dividers>
        <Elements
          stripe={stripePromise}
          options={{
            fonts: [
              {
                cssSrc:
                  'https://fonts.googleapis.com/css2?family=Arimo:wght@400&display=swap',
              },
            ],
          }}
        >
          <CheckoutForm onToken={onSubmit} />
        </Elements>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onCancel}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Subscription = ({ user }) => {
  const classes = useStyles();
  const [customer, setCustomer] = useState();
  const [showModal, setShowModal] = useState(false);
  const { data, loading, error } = useQuery(GET_SUBSCRIPTION_DETAILS, {
    variables: { studentId: user.id },
  });
  const snack = useSnack();
  useEffect(() => {
    if (data && data.subscriptions.length) {
      const subscription = data.subscriptions[0];
      if (!subscription.stripe_customer_id) return;
      API.get(
        'api',
        `/stripe/customers/${subscription.stripe_customer_id}`,
      ).then(
        (resp) => {
          setCustomer(resp);
        },
        (e) => {
          console.log(e);
          snack.error('Error loading card details');
        },
      );
    }
  }, [data]);

  const onPortalSession = () => {
    return API.post(
      'api',
      `/stripe/portal_session/${subscription.stripe_customer_id}`,
    ).then(
      (resp) => {
        window.location = resp.redirect;
      },
      (e) => {
        snack.error(e.message);
      },
    );
  };

  const onToken = (token) => {
    console.log(token);
    return API.post('api', `/users/upgrade_trial`, {
      body: {
        studentId: user.id,
        planId: subscription.plan_id,
        token,
      },
    }).then(
      (resp) => {
        snack.success('Thank you! Your subscription is active.');
        setShowModal(false);
        window.location.reload();
      },
      (e) => {
        snack.error(e.message);
      },
    );
  };

  if (loading) return <Spin spinning />;
  if (error) return error.message;

  const subscription = data.subscriptions[0];

  console.log(subscription)

  if (!subscription)
    return (
      <Alert severity="error">
        No Subscription found. Please contact support.
      </Alert>
    );

  const source = customer?.sources.data[0];
  const stripeSub = customer?.subscriptions.data[0];

  return (
    <Box>
      <Card className={classes.card}>
        <CardContent>
          <Typography paragraph variant="h5">
            Subscription Details
          </Typography>
          <Typography variant="smallBold" color="textSecondary">
            Subscription
          </Typography>
          <Typography variant="body2">
            {subscription.product.package_name}
          </Typography>
          {subscription.status === 'expired' ? (
            <Box mt={2}>
              <Alert severity="error">
                Your subscription has been cancelled.
              </Alert>
              {subscription.product.pricing_model === 'Perpetual' && (
                <Box mt={2}>
                  <Button
                    onClick={() => setShowModal(true)}
                    variant="contained"
                    color="primary"
                  >
                    Subscribe again
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <>
              {customer && (
                <Box mt={1}>
                  <Typography variant="smallBold" color="textSecondary">
                    Payment Method
                  </Typography>
                  <Typography gutterBottom variant="body2">
                    <span style={{ textTransform: 'uppercase' }}>
                      {source.brand}
                    </span>{' '}
                    ending with {source.last4}
                  </Typography>
                  {stripeSub && (
                    <Typography
                      variant="caption"
                      component="p"
                      color="textSecondary"
                    >
                      Your next billing date is{' '}
                      {toUserTimezone(
                        customer.subscriptions.data[0].current_period_end *
                          1000,
                        user.timezone,
                      ).format('MMMM D, YYYY')}
                    </Typography>
                  )}
                </Box>
              )}
              {stripeSub && subscription.product.pricing_model === 'Perpetual' && (
                <Box mt={2}>
                  <Button
                    onClick={onPortalSession}
                    variant="contained"
                    color="primary"
                  >
                    Manage Subscription
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
      {showModal && (
        <ReSubModal
          product={subscription.product}
          onCancel={() => setShowModal(false)}
          onSubmit={onToken}
        />
      )}
    </Box>
  );
};

export default Subscription;
